import { z } from 'zod'

export const SCHEDULE_EVENT_CATEGORIES = [
  'preparation',
  'ceremony',
  'photos',
  'food',
  'program',
  'music',
  'logistics',
  'supplier',
  'other',
] as const

export const scheduleEventCategorySchema = z.enum(SCHEDULE_EVENT_CATEGORIES)

const optionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.string().trim().max(max).optional(),
  )

const clearableOptionalText = (max: number) =>
  z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
    z.string().trim().max(max).nullable().optional(),
  )

const optionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.iso.date().optional(),
)

const clearableOptionalDate = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? null : value),
  z.iso.date().nullable().optional(),
)

export const scheduleColorSchema = z
  .string()
  .trim()
  .regex(/^#[0-9a-fA-F]{6}$/, 'Use #RRGGBB color')

const startMinuteSchema = z.number().int().min(0).max(1795)
const endMinuteSchema = z.number().int().min(5).max(1800)

function validateMinuteRange(
  value: { startMinute?: number; endMinute?: number },
  ctx: z.RefinementCtx,
) {
  if (value.startMinute === undefined || value.endMinute === undefined) {
    return
  }

  if (value.endMinute <= value.startMinute) {
    ctx.addIssue({
      code: 'custom',
      path: ['endMinute'],
      message: 'End time must be after start time',
    })
    return
  }

  if (value.endMinute - value.startMinute > 1440) {
    ctx.addIssue({
      code: 'custom',
      path: ['endMinute'],
      message: 'Event cannot be longer than 24 hours',
    })
  }
}

export const createScheduleEventSchema = z
  .object({
    title: z.string().trim().min(1, 'Enter event title').max(180),
    description: optionalText(500),
    category: scheduleEventCategorySchema.default('program'),
    date: optionalDate,
    startMinute: startMinuteSchema,
    endMinute: endMinuteSchema,
    location: optionalText(180),
    notes: optionalText(3000),
    color: scheduleColorSchema.optional(),
    sortOrder: z.number().int().min(0).max(10_000).default(0),
  })
  .superRefine(validateMinuteRange)

export const updateScheduleEventSchema = z
  .object({
    title: z.string().trim().min(1, 'Enter event title').max(180).optional(),
    description: clearableOptionalText(500),
    category: scheduleEventCategorySchema.optional(),
    date: clearableOptionalDate,
    startMinute: startMinuteSchema.optional(),
    endMinute: endMinuteSchema.optional(),
    location: clearableOptionalText(180),
    notes: clearableOptionalText(3000),
    color: scheduleColorSchema.nullable().optional(),
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .superRefine((value, ctx) => {
    if (
      (value.startMinute === undefined && value.endMinute !== undefined) ||
      (value.startMinute !== undefined && value.endMinute === undefined)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['startMinute'],
        message: 'Start and end time must be changed together',
      })
      return
    }

    validateMinuteRange(value, ctx)
  })

export const rescheduleScheduleEventSchema = z
  .object({
    date: clearableOptionalDate,
    startMinute: startMinuteSchema,
    endMinute: endMinuteSchema,
    sortOrder: z.number().int().min(0).max(10_000).optional(),
  })
  .superRefine(validateMinuteRange)

export const reorderScheduleEventsSchema = z.object({
  events: z
    .array(
      z.object({
        id: z.uuid(),
        sortOrder: z.number().int().min(0).max(10_000),
      }),
    )
    .min(1)
    .max(200),
})

export type ScheduleEventCategory = (typeof SCHEDULE_EVENT_CATEGORIES)[number]
export type CreateScheduleEventInput = z.infer<typeof createScheduleEventSchema>
export type UpdateScheduleEventInput = z.infer<typeof updateScheduleEventSchema>
export type RescheduleScheduleEventInput = z.infer<typeof rescheduleScheduleEventSchema>
export type ReorderScheduleEventsInput = z.infer<typeof reorderScheduleEventsSchema>
